"use client";

import React from "react";
import { ShieldCheck, CircleCheck, Lock, FileText, Eye } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const protocols = [{
  id: "consentimiento",
  title: "Consentimiento Informado",
  icon: FileText,
  bullets: ["Aprobación previa y documentada para cada gestión importante.", "Claridad absoluta en los alcances y límites del servicio.", "Respeto irrestricto por la autonomía y decisiones del adulto mayor."]
}, {
  id: "doble-validacion",
  title: "Doble Validación",
  icon: CircleCheck,
  bullets: ["Verificación estricta de identidad para cuidadores y especialistas.", "Confirmación cruzada de citas médicas, traslados y entregas.", "Notificaciones en tiempo real a la familia ante cualquier eventualidad."]
}, {
  id: "registro-auditable",
  title: "Registro Auditable",
  icon: Eye,
  bullets: ["Historial detallado y cronológico de cada intervención realizada.", "Reportes periódicos de salud, bienestar y gestiones logísticas.", "Acceso transparente a la información para los familiares autorizados."]
}, {
  id: "politica-antifraude",
  title: "Política Anti-Fraude",
  icon: Lock,
  bullets: ["Protección rigurosa de datos personales, médicos y financieros.", "Asesoría y acompañamiento en la prevención de estafas digitales.", "Protocolos estrictos y seguros para el manejo de claves y accesos."]
}];
export default function ProtocolosDeConfianza() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="protocolos-de-confianza" className="relative w-full bg-background py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05]" style={{
      backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
      backgroundSize: `32px 32px`,
      maskImage: `radial-gradient(circle at center, black 40%, transparent 80%)`,
      WebkitMaskImage: `radial-gradient(circle at center, black 40%, transparent 80%)`
    }} aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-4 border-primary/20 bg-primary/5 text-primary px-4 py-1.5 text-sm rounded-full">
            <ShieldCheck className="w-4 h-4 mr-2 inline-block" />
            Promesa de Confianza
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-6">
            Seguridad y Trazabilidad
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Implementamos protocolos rigurosos para garantizar la tranquilidad de tu familia. 
            Cada acción está documentada, validada y orientada a proteger el bienestar de quienes más quieres.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {protocols.map((protocol, i) => {
          const Icon = protocol.icon;
          return <Card key={protocol.id} data-index={i} className="bg-card/80 backdrop-blur-sm border-border/50 shadow-sm hover:shadow-md transition-all duration-300 group">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-6 h-6" />
                    </div>
                    <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20 border-0 font-medium">
                      <ShieldCheck className="w-3 h-3 mr-1" />
                      Verificado
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground">
                    {protocol.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {protocol.bullets.map((bullet, bulletIndex) => <li key={bulletIndex} className="flex items-start gap-3 text-muted-foreground">
                        <CircleCheck className="w-5 h-5 text-primary/60 shrink-0 mt-0.5" />
                        <span className="text-sm leading-relaxed">{bullet}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}